import { FileText, Image as ImageIcon, File as FileIcon, Music } from 'lucide-react';
import type { UploadPhase } from '../../lib/fileUpload';
import type { UploadTask } from '../../hooks/useUploadQueue';
import { useI18n } from '../../contexts/I18nContext';

/**
 * Shared upload-progress UI.
 *
 * Every upload surface in the app used to say the same thing while it worked —
 * "Se încarcă…" next to a disabled dropzone — which told the person nothing
 * about how long a 30 MB recording would take, or whether it had stalled. These
 * components give one honest picture instead: the file being sent, how far it
 * has got, and which of the two waits it is in (bytes moving, or the server
 * working on what it received).
 *
 * Three shapes for three contexts:
 *   - {@link UploadProgressList} — rows under a dropzone (resource tabs,
 *     onboarding, bank statements).
 *   - {@link UploadChip}        — a compact pill for message composers.
 *   - {@link UploadProgressBar} — the bare bar, for anything else.
 */

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function formatUploadBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[i]}`;
}

function UploadFileIcon({ name, className }: { name: string; className?: string }) {
  const ext = (name.split('.').pop() ?? '').toLowerCase();
  if (ext === 'pdf') return <FileText className={cx(className, 'text-red-500')} />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(ext)) {
    return <ImageIcon className={cx(className, 'text-blue-500')} />;
  }
  if (['mp3', 'm4a', 'wav', 'ogg', 'webm', 'aac'].includes(ext)) {
    return <Music className={cx(className, 'text-violet-500')} />;
  }
  return <FileIcon className={cx(className, 'text-gray-400')} />;
}

/* ------------------------------------------------------------------------- */
/* Bar                                                                        */
/* ------------------------------------------------------------------------- */

interface UploadProgressBarProps {
  percent: number;
  phase: UploadPhase;
  className?: string;
}

/**
 * The bar itself. Determinate while bytes move; a sliding fill once they are
 * all out, because the remaining wait has no percentage to show.
 */
export function UploadProgressBar({ percent, phase, className }: UploadProgressBarProps) {
  const { t } = useI18n();
  const processing = phase === 'processing';
  return (
    <div
      className={cx('h-1.5 w-full overflow-hidden rounded-full bg-gray-200', className)}
      role="progressbar"
      aria-label={t('upload.progressLabel')}
      // An indeterminate bar announces itself by omitting aria-valuenow.
      aria-valuenow={processing ? undefined : percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cx(
          'h-full rounded-full bg-brand-600',
          processing ? 'ui-progress-indeterminate' : 'transition-[width] duration-200 ease-out',
        )}
        style={processing ? undefined : { width: `${Math.max(2, percent)}%` }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Rows                                                                       */
/* ------------------------------------------------------------------------- */

/** Human-readable state line: "1.2 MB / 3.4 MB", or what the server is doing. */
function useStatusLine(upload: UploadTask, processingLabel?: string): string {
  const { t } = useI18n();
  if (upload.phase === 'processing') return processingLabel ?? t('upload.processing');
  if (upload.total <= 0) return t('upload.uploading');
  return `${formatUploadBytes(upload.loaded)} / ${formatUploadBytes(upload.total)}`;
}

export function UploadProgressRow({
  upload,
  processingLabel,
}: {
  upload: UploadTask;
  /**
   * What the server is doing once the bytes are in, when the caller knows —
   * "parsing the statement", "transcribing the recording". Without it the row
   * says the generic "finishing on the server".
   */
  processingLabel?: string;
}) {
  const status = useStatusLine(upload, processingLabel);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <UploadFileIcon name={upload.name} className="h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="truncate text-sm font-medium text-gray-900">{upload.name}</p>
          <span className="shrink-0 text-xs tabular-nums text-gray-500">
            {upload.phase === 'processing' ? '' : `${upload.percent}%`}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-500">{status}</p>
        <UploadProgressBar className="mt-1.5" percent={upload.percent} phase={upload.phase} />
      </div>
    </div>
  );
}

/** Stacked rows, one per in-flight file. Renders nothing when idle. */
export function UploadProgressList({
  uploads,
  className,
  processingLabel,
}: {
  uploads: UploadTask[];
  className?: string;
  processingLabel?: string;
}) {
  if (uploads.length === 0) return null;
  return (
    <div className={cx('space-y-2', className)} aria-live="polite">
      {uploads.map((u) => (
        <UploadProgressRow key={u.id} upload={u} processingLabel={processingLabel} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Chip                                                                       */
/* ------------------------------------------------------------------------- */

/**
 * Compact pill for message composers, where an in-flight attachment sits in the
 * same row as the finished ones. Shows the real file name instead of a generic
 * placeholder, so a person who picked three files can tell which is still going.
 */
export function UploadChip({ upload, className }: { upload: UploadTask; className?: string }) {
  const { t } = useI18n();
  return (
    <span
      className={cx(
        'inline-flex max-w-52 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1',
        className,
      )}
      title={
        upload.phase === 'processing'
          ? `${upload.name} — ${t('upload.processing')}`
          : `${upload.name} — ${upload.percent}%`
      }
    >
      <UploadFileIcon name={upload.name} className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs text-gray-700">{upload.name}</span>
        <UploadProgressBar className="mt-1 h-1" percent={upload.percent} phase={upload.phase} />
      </span>
      <span className="shrink-0 text-[10px] tabular-nums text-gray-500">
        {upload.phase === 'processing' ? '…' : `${upload.percent}%`}
      </span>
    </span>
  );
}

/** Chips for every in-flight upload. Renders nothing when idle. */
export function UploadChipList({
  uploads,
  className,
}: {
  uploads: UploadTask[];
  className?: string;
}) {
  if (uploads.length === 0) return null;
  return (
    <>
      {uploads.map((u) => (
        <UploadChip key={u.id} upload={u} className={className} />
      ))}
    </>
  );
}
